import {useState} from 'react';
import Icon from '../Icon';
import Sheet from '../feedback/Sheet';
import KindChips from './KindChips';
import {usePlaces} from '../../api/places';
import {usePlants} from '../../api/posts';
import {useMe} from '../../hooks/useAuth';
import {KIND_DETAILS} from '../../lib/kinds';
import {groupPlaces} from '../../lib/placeTree';
import {seasonRange, yearRange} from '../../lib/season';
import {FeedFilters} from '../../types';

// "What did I plant in the back bed, and when?" without a search box (wireframe #filter): kind
// chips, your plants with counts, your places by area, and a few date presets.
type Props = {open: boolean; onClose: () => void; filters: FeedFilters; onApply: (filters: FeedFilters) => void};

export default function FilterSheet({open, onClose, filters, onApply}: Props) {
    const me = useMe();
    const plants = usePlants();
    const places = usePlaces(false);
    const [draft, setDraft] = useState<FeedFilters>(filters);

    const season = seasonRange(new Date(), me.data?.hemisphere);
    const year = yearRange(new Date());
    const dateIs = (range: {from: string; to: string} | null) => (range ? draft.from === range.from && draft.to === range.to : !draft.from && !draft.to);
    const setDate = (range: {from: string; to: string} | null) => setDraft(d => ({...d, from: range?.from ?? null, to: range?.to ?? null}));

    const chip = (selected: boolean, label: string, onClick: () => void, key: string) => (
        <button key={key} type="button" className="chip" aria-pressed={selected} onClick={onClick}>
            {selected && <Icon name="check" size={16} strokeWidth={2.2} />}
            {label}
        </button>
    );

    return (
        <Sheet open={open} onClose={onClose} title="Find tells">
            <div className="flex flex-col gap-5">
                <section className="flex flex-col gap-2">
                    <span className="band-label text-ink-2">Kind</span>
                    <KindChips value={draft.kind ?? null} onChange={kind => setDraft(d => ({...d, kind, detail: null}))} />
                    {draft.kind && (KIND_DETAILS[draft.kind]?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {KIND_DETAILS[draft.kind]!.map(detail => chip(draft.detail === detail, detail, () => setDraft(d => ({...d, detail: d.detail === detail ? null : detail})), detail))}
                        </div>
                    )}
                </section>

                {(plants.data?.length ?? 0) > 0 && (
                    <section className="flex flex-col gap-2">
                        <span className="band-label text-ink-2">Plant</span>
                        <div className="flex flex-wrap gap-2">
                            {plants.data!.filter(p => p.tellCount > 0).map(p => chip(draft.plantId === p.id, `${p.label} · ${p.tellCount}`, () => setDraft(d => ({...d, plantId: d.plantId === p.id ? null : p.id})), p.id))}
                        </div>
                    </section>
                )}

                <section className="flex flex-col gap-2">
                    <span className="band-label text-ink-2">Place</span>
                    <div className="flex flex-wrap gap-2">
                        {groupPlaces(places.data ?? []).flatMap(({area, spots}) => [
                            chip(draft.placeId === area.id, `${area.name} · ${area.tellCount}`, () => setDraft(d => ({...d, placeId: d.placeId === area.id ? null : area.id})), area.id),
                            ...spots.map(s => chip(draft.placeId === s.id, `${s.name} · ${s.tellCount}`, () => setDraft(d => ({...d, placeId: d.placeId === s.id ? null : s.id})), s.id))
                        ])}
                    </div>
                </section>

                <section className="flex flex-col gap-2">
                    <span className="band-label text-ink-2">When</span>
                    <div className="flex flex-wrap gap-2">
                        {chip(dateIs(null), 'All time', () => setDate(null), 'all')}
                        {chip(dateIs(season), `This ${season.name}`, () => setDate(season), 'season')}
                        {chip(dateIs(year), 'This year', () => setDate(year), 'year')}
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="date" className="field min-h-[44px] flex-1" value={draft.from ?? ''} onChange={e => setDraft(d => ({...d, from: e.target.value || null}))} aria-label="From" />
                        <span className="meta">to</span>
                        <input type="date" className="field min-h-[44px] flex-1" value={draft.to ?? ''} onChange={e => setDraft(d => ({...d, to: e.target.value || null}))} aria-label="To" />
                    </div>
                </section>

                <div className="flex gap-2">
                    <button type="button" className="btn btn-secondary flex-1" onClick={() => setDraft({})}>
                        Clear
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary flex-[2]"
                        onClick={() => {
                            onApply(draft);
                            onClose();
                        }}
                    >
                        Show tells
                    </button>
                </div>
            </div>
        </Sheet>
    );
}
